import React, { useState, useCallback, useMemo, useEffect } from 'react'; 
import { useTranslation } from 'react-i18next';
import HeaderProfile from '../../components/header_Profile/header_Profile.index';
import ProcedureList from '../../components/treatmentPlan/Procedure/procedureList';
import {  Tab, Tabs, ThemeProvider } from '@mui/material';
import { theme } from '../../theme';
import { getStoredPermissions, tabStyles } from '../../utils/commonUtils';
import DrugsHome from '../../components/treatmentPlan/Drugs/DrugsHome';
import { useLocation } from 'react-router-dom';
import { usePrint } from '../../components/PrintProvider';
import { PatientData, PermissionsData, TreatmentPlanPermission, TreatmentPlanTypePermission } from './treatmentPlan.helper';
import NoAccessPage from '../noAccessPage';

const TreatmentPlanPage: React.FC = () => {
  const { t: tGeneral } = useTranslation('general');
  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>(null);
  const location = useLocation();

  const allowedTreatMentPlanPermissions: TreatmentPlanPermission | { type: [] } = React.useMemo(() => {
    const permissionsData = getStoredPermissions() as PermissionsData | undefined;
    const clinicalModule = permissionsData?.modules?.find(
      (m) => m.name === 'Clinical' && m.featureEnabled
    );
    if (!clinicalModule) return { type: [] };
    const TreatmentModule = clinicalModule.submodules.find(
      (sm) => sm.name === 'Treatment Plan'
    );
    return TreatmentModule || { type: [] };
  }, []);

  const procedureTabObj = Array.isArray(allowedTreatMentPlanPermissions.type)
    ? allowedTreatMentPlanPermissions.type.find((t: TreatmentPlanTypePermission) => t.name === 'Procedure')
    : undefined;
  const drugTabObj = Array.isArray(allowedTreatMentPlanPermissions.type)
    ? allowedTreatMentPlanPermissions.type.find((t: TreatmentPlanTypePermission) => t.name === 'Drug')
    : undefined;

  const availableTabs = [];
  if (procedureTabObj) availableTabs.push('procedure');
  if (drugTabObj) availableTabs.push('drug');
  const [tabIndex, setTabIndex] = useState(availableTabs.length > 0 ? 0 : -1); // -1 means no tab


  const handleDatafromHeader = useCallback((data: PatientData) => {
    setSelectedPatient(data);
  }, []);

  const {TabOption, setTabOption} = usePrint();

  const updateQueryParam = (key: string, value: string | null): void => {
    const queryParams = new URLSearchParams(location.search);
    if (value) {
      queryParams.set(key, value); 
    } else {
      queryParams.delete(key);
    }
    window.history.replaceState(null, '', `${location.pathname}?${queryParams.toString()}`);
  };

  const viewMapping: Record<number, string | null> = useMemo(() => ({
    0: 'procedure_list',
    1: 'drug_list',
  }), []);
  

  // Handle tab change
  const handleChangeTab = (event: React.SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
    if (Object.hasOwn(viewMapping, newTabIndex)) {
      updateQueryParam('view', viewMapping[newTabIndex]);
    }
  };

  const renderTabContent = useMemo(() => {
    if (!selectedPatient) {
      return <p>{tGeneral('noPatientSelected')}</p>;
    }

    const tabConfig = [];
    if (procedureTabObj) {
      tabConfig.push({
        obj: procedureTabObj,
        component: <ProcedureList patientId={selectedPatient.id} permissionObj={procedureTabObj} />,
      });
    }
    if (drugTabObj) {
      tabConfig.push({
        obj: drugTabObj,
        component: <DrugsHome permissionObj={drugTabObj} />,
      });
    }

    if (tabIndex === -1 || !tabConfig[tabIndex]) {
      return <p style={{ color: '#e53935', fontWeight: 600, fontSize: '1.2rem', textAlign: 'center', marginTop: '2rem' }}>No available tabs for this user.</p>;
    }

    const currentTab = tabConfig[tabIndex];
    if (currentTab && currentTab.obj) {
      if (selectedPatient.gender === 'FEMALE' && currentTab.obj.female) {
        return currentTab.component;
      }
      if (selectedPatient.gender === 'MALE' && currentTab.obj.male) {
        return currentTab.component;
      }
      return <NoAccessPage />;
    }
    return null;
  }, [selectedPatient, tabIndex, procedureTabObj, drugTabObj, tGeneral]);

  useEffect(() => { 
    setTabOption({...TabOption, subOption:  viewMapping[tabIndex]});
  // eslint-disable-next-line
  }, [tabIndex]);
  return (
    <ThemeProvider theme={theme}>
      <div>
        <HeaderProfile onData={handleDatafromHeader} />
        {availableTabs.length > 0 && (
          <Tabs
            value={tabIndex}
            onChange={handleChangeTab}
            aria-label="Treatment Plan tabs"
            variant="fullWidth"
            sx={tabStyles}
          >
            {procedureTabObj && <Tab label={tGeneral('procedure')} />}
            {drugTabObj && <Tab label={tGeneral('drugs')} />}
          </Tabs>
        )}
        <div className="mt-4">
          {renderTabContent}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default TreatmentPlanPage;
